import { Button, Col, message, Select } from "antd";
import SubmitOneTimeBilling from "common/submitOneTimeBilling/submitOneTimeBilling";
import BulkUpdateInvoice from "features/billingCommissions/invoices/bulkUpdateInvoice";
import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Invoice from "redux/models/invoice";
import { selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import Export from "redux/models/export";
import { usePageFilter } from "hooks/usePageFilters";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { ServerSideDataGrid } from "common/components/serverSideDataGrid/serverSideDataGrid";
import { ExportDataButton } from "common/components/ExportDataButton";
import AgGridUtility from "utilities/agGridUtility";
import { Columns } from "./columns";

export const InvoicesContainer = ({
  type,
  clientIds,
  selectedClientId,
  tableConfigKey,
  filtersPrefix,
}) => {
  const history = useHistory();

  const userId = useSelector(selectUserId);

  const gridRef = useRef(null);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [search, setSearch] = usePageFilter(`${filtersPrefix}_search`);
  const [status, setStatus] = usePageFilter(`${filtersPrefix}_status`);

  const [selectedRows, setSelectedRows] = useState({
    rowIds: [],
    rowData: [],
    isDisabled: true,
    isModalVisible: false,
    shouldUpdate: false,
  });

  const [totalCount, setTotalCount] = useState(null);

  const isExportDisabled = totalCount === 0;

  const show = () => {
    setIsModalVisible(true);
  };

  const showBulkUpdate = () => {
    setSelectedRows((prev) => ({
      ...prev,
      isModalVisible: true,
    }));
  };

  const rowClickedHandler = (params) => {
    history.push(`/billings/invoices/detail/${params.data.id}`);
  };

  const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const isSameStatus = selectedRows.every((obj, index, array) => {
      return obj.status === array[0].status;
    });
    if (!isSameStatus) {
      message.warning(
        "You have selected two invoices with different statuses for bulk update.",
      );
    }

    setSelectedRows((prev) => ({
      ...prev,
      isDisabled: selectedRows.length === 0,
      rowData: selectedRows,
      rowIds: selectedRows.map(({ id }) => id),
    }));
  };

  const getServerSideData = useCallback(
    async (options) => {
      return Invoice.v2.getAll({
        ...options,
        type,
        client: clientIds,
        status: status?.value,
        search: search ?? undefined,
      });
    },
    [search, status?.value, clientIds, type],
  );

  const onDataFetchSuccess = useCallback(
    (data) => {
      setTotalCount(data.rowCount);
    },
    [setTotalCount],
  );

  const onBulkUpdateSuccess = (data) => {
    AgGridUtility.onServerSideRowsUpdated(
      gridRef.current?.api,
      selectedRows.rowData.map((r) => ({ ...r, status: data.status })),
    );

    gridRef.current?.api?.deselectAll();
  };

  const onExportRequested = async () => {
    try {
      const { columnApi } = gridRef.current;
      const sortColumn = columnApi.getColumnState().find((c) => c.sort);

      const params = {
        type,
        client: clientIds,
        status: status?.value,
        search: search ?? undefined,
        sortBy: sortColumn?.colId,
        sortOrder: sortColumn?.sort,
      };

      await Export.getReportInvoices(params);

      message.success(
        "Invoices export request successfully received! Check your email shortly",
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <DataTableLayout
        ref={gridRef}
        searchDefaultValue={search}
        onSearchChange={setSearch}
        renderFilters={() => (
          <Col style={{ width: 200 }}>
            <Select
              allowClear
              showSearch
              defaultValue={status}
              placeholder="Status"
              style={{ width: "100%" }}
              optionFilterProp="label"
              options={Object.keys(K.Invoice.Status).map((item) => ({
                value: K.Invoice.Status[item],
                label: item,
              }))}
              onChange={(statusValue, status) => setStatus(status)}
            />
          </Col>
        )}
        actions={[
          {
            renderer: () => (
              <Col>
                <ExportDataButton
                  onClick={onExportRequested}
                  disabled={isExportDisabled}
                />
              </Col>
            ),
          },
          {
            permissions: [K.Permissions.EditInvoice],
            renderer: () => (
              <Col>
                <Button
                  type="primary"
                  onClick={showBulkUpdate}
                  disabled={selectedRows.isDisabled}
                >
                  Bulk Update
                </Button>
              </Col>
            ),
          },
          {
            hide: type !== K.Invoice.Type.Client,
            permissions: [
              K.Permissions.CreateOneTimeBilling,
            ],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={show}>
                  Submit One Time Billing
                </Button>
              </Col>
            ),
          },
        ]}
      >
        <ServerSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={tableConfigKey}
          getData={getServerSideData}
          onDataFetchSuccess={onDataFetchSuccess}
          defaultColumnConfig={Columns}
          onSelectionChanged={onSelectionChanged}
          onRowClicked={rowClickedHandler}
          suppressRowClickSelection
          rowSelection="multiple"
          rowStyle={{ cursor: "pointer" }}
        />
      </DataTableLayout>

      <SubmitOneTimeBilling
        selectedClientId={selectedClientId}
        isOpen={isModalVisible}
        setIsModalVisible={setIsModalVisible}
      />

      <BulkUpdateInvoice
        isOpen={selectedRows.isModalVisible}
        setIsModalVisible={setSelectedRows}
        modalData={selectedRows}
        onSuccess={onBulkUpdateSuccess}
      />
    </>
  );
};
